import {
  Box,
  type BoxProps,
  type ElementProps,
  rem,
  Stack,
  type StackProps,
  Text,
} from "@mantine/core";
import { IconArrowLeft, IconChevronRight } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { IntegrationLogo } from "../IntegrationLogo";
import classes from "./index.module.css";

/**
 * Peças de página compartilhadas por TODAS as telas de integração (detalhe,
 * instalação, conexão, conclusão). É aqui que vive a rima visual: peso de
 * fonte, raio, borda e espaçamento são decididos uma vez e reusados. Uma tela
 * nova de integração monta a partir destas peças, não de estilos próprios.
 */

/**
 * Contêiner de toda tela de integração: largura da página, respiro e a escala
 * de texto destas telas (ver `.scope` no CSS). Existe para essas três coisas
 * ficarem num lugar só, em vez de repetidas em cada `return` das páginas.
 *
 * O teto de 1200px é o mesmo do Agente, Contatos e Configurações. Sem ele a
 * página ia até a borda do monitor: num 1920 o cartão ficava com 1624px, e um
 * parágrafo ali dentro passa de 180 caracteres por linha, que ninguém lê sem
 * perder o começo da linha seguinte.
 */
export function IntegrationPage({
  children,
  ...others
}: { children: ReactNode } & Omit<StackProps, "children">) {
  return (
    <Stack
      p="xl"
      maw={rem(1200)}
      mx="auto"
      w="100%"
      className={classes.scope}
      {...others}>
      {children}
    </Stack>
  );
}

export interface BreadcrumbItem {
  label: string;
  /** Sem `to`, o item é o atual (texto forte, não clicável). */
  to?: string;
}

export function IntegrationBreadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav
      aria-label="Caminho"
      className={`${classes.breadcrumb} ${classes.textMuted}`}>
      <IconArrowLeft
        size={14}
        aria-hidden
      />
      {items.map((item, index) => (
        <Box
          key={`${item.label}-${index}`}
          component="span"
          className={classes.breadcrumb}>
          {index > 0 && (
            <IconChevronRight
              size={12}
              aria-hidden
            />
          )}
          {item.to ? (
            <Link
              to={item.to}
              className={classes.breadcrumbLink}>
              {item.label}
            </Link>
          ) : (
            <Text
              component="span"
              inherit
              className={classes.textMain}
              aria-current="page">
              {item.label}
            </Text>
          )}
        </Box>
      ))}
    </nav>
  );
}

export function IntegrationHeader({
  slug,
  logoText,
  logoColor,
  title,
  badge,
  caption,
  children,
  actions,
}: {
  slug: string;
  logoText: string;
  logoColor: string;
  title: ReactNode;
  /** Estado ao lado do título (Disponível, Ativa, Erro). */
  badge?: ReactNode;
  /** Linha logo abaixo do título ("por Google · Agendamento" ou uma frase). */
  caption: ReactNode;
  /** Linhas extras sob a caption (link de documentação, última verificação). */
  children?: ReactNode;
  /** Lado direito: botões ou uma pílula informativa. */
  actions?: ReactNode;
}) {
  return (
    <header className={classes.header}>
      <div className={classes.headerIdentity}>
        <IntegrationLogo
          slug={slug}
          logoText={logoText}
          logoColor={logoColor}
          size="lg"
        />
        <Stack gap={4}>
          <div className={classes.headerTitleRow}>
            <Text
              component="h1"
              className={`${classes.headerTitle} ${classes.textMain}`}>
              {title}
            </Text>
            {badge}
          </div>
          <Text
            size="sm"
            className={`${classes.textMuted} ${classes.cardDescription}`}>
            {caption}
          </Text>
          {children}
        </Stack>
      </div>
      {actions && <div className={classes.headerActions}>{actions}</div>}
    </header>
  );
}

interface IntegrationCardProps
  extends BoxProps, Omit<ElementProps<"section">, keyof BoxProps | "title"> {
  title?: ReactNode;
  /** Ícone à esquerda do título, na cor da marca. */
  icon?: ReactNode;
  /** Elemento à direita do título (badge de contagem, botão "Alterar"). */
  action?: ReactNode;
  description?: ReactNode;
  /** `brand` destaca o cartão com a borda roxa. */
  tone?: "default" | "brand";
  /** Sem padding: o conteúdo controla o próprio espaçamento (palco, colunas). */
  flush?: boolean;
  children?: ReactNode;
}

export function IntegrationCard({
  title,
  icon,
  action,
  description,
  tone = "default",
  flush = false,
  children,
  className,
  ...others
}: IntegrationCardProps) {
  const hasHeader = Boolean(title || description || action);
  return (
    <Box
      component="section"
      className={[classes.card, className].filter(Boolean).join(" ")}
      data-tone={tone}
      data-flush={flush}
      {...others}>
      {/* Sem padding, também sem gap: abas, palco e rodapé colam uns nos outros. */}
      <Stack gap={flush ? 0 : "1rem"}>
        {hasHeader && (
          <Stack gap={4}>
            <div className={classes.cardHeader}>
              <div className={classes.cardTitleRow}>
                {icon && <span className={classes.cardIcon}>{icon}</span>}
                {title && (
                  <Text
                    component="h2"
                    className={`${classes.cardTitle} ${classes.textMain}`}>
                    {title}
                  </Text>
                )}
              </div>
              {action}
            </div>
            {description && (
              <Text
                size="sm"
                className={`${classes.textMuted} ${classes.cardDescription}`}>
                {description}
              </Text>
            )}
          </Stack>
        )}
        {children}
      </Stack>
    </Box>
  );
}

/** Rótulo pequeno em caixa alta ("PASSO 1 DE 2", "CONTA GOOGLE"). */
export function Eyebrow({
  tone = "muted",
  children,
  className,
}: {
  tone?: "brand" | "muted";
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={[classes.eyebrow, className].filter(Boolean).join(" ")}
      data-tone={tone}>
      {children}
    </span>
  );
}

/** Pílula informativa do cabeçalho ("Conecta em menos de 1 minuto"). */
export function InfoPill({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={`${classes.pill} ${classes.textMain}`}>
      {icon && <span className={classes.pillIcon}>{icon}</span>}
      {children}
    </span>
  );
}

/** Lista numerada com o índice na cor da marca (pré-requisitos). */
export function NumberedList({ items }: { items: string[] }) {
  return (
    <ol className={`${classes.numberedList} ${classes.textMain}`}>
      {items.map((item, index) => (
        <li
          key={item}
          className={classes.numberedItem}>
          <span className={classes.numberedIndex}>{index + 1}</span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

/** Classes de texto do chrome, para componentes irmãos que só precisam da cor. */
export const chromeClasses = classes;
